import { ArrowRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from '@kostbar/i18n'
import type {
  ReportMeasure,
  ReportRow,
  ReportTotals,
} from '@/features/reports/api/use-sales-report-query'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { formatCurrency, formatNumber } from '@/features/reports/lib/format'

/** Fixed order and fixed colour per channel — never by rank, so a hue always means one thing. */
const CHANNEL_ORDER = ['SELF_CHECKOUT', 'STAFF', 'ONLINE', 'UNKNOWN'] as const

const CHANNEL_COLOR: Record<string, string> = {
  SELF_CHECKOUT: 'var(--channel-self)',
  STAFF: 'var(--channel-staff)',
  ONLINE: 'var(--channel-online)',
  // A neutral, not a fourth hue: this bucket is the absence of operator data on POS sales from
  // before 2026-09-03, and an equal-weight colour would make missing history look like a segment.
  UNKNOWN: 'var(--channel-unknown)',
}

interface ChannelMixCardProps {
  rows: Array<ReportRow>
  total: ReportTotals
  measure: ReportMeasure
  detailsTo: string
  /** Router search params, so a tile hands its own slice to the page it drills into. */
  detailsSearch?: Record<string, unknown>
  loading?: boolean
}

/**
 * "Where does the revenue come from" (CHG-2026-09-manager-reporting).
 *
 * Self-service, staffed, online — and, when it occurs, a fourth row for sales whose operator was
 * never recorded. That row is shown rather than folded into self-service: it is missing data, and
 * every POS sale from before 2026-09-03 has it.
 *
 * Table sales roll up under "Bedient" for now. `pos_sales.table_number` is captured, so the data is
 * there; making Tisch its own channel is a later change, and the note says so rather than leaving
 * the reader to wonder where table revenue went.
 */
export function ChannelMixCard({
  rows,
  total,
  measure,
  detailsTo,
  detailsSearch,
  loading,
}: ChannelMixCardProps) {
  const { t } = useTranslation('reports')
  const value = (row: ReportRow) =>
    measure === 'units' ? row.units : row.revenueCents
  const totalValue = measure === 'units' ? total.units : total.revenueCents
  const format = (n: number) =>
    measure === 'units' ? formatNumber(n) : formatCurrency(n)

  const ordered = CHANNEL_ORDER.map((key) =>
    rows.find((r) => r.key === key),
  ).filter((row): row is ReportRow => row != null && value(row) > 0)

  return (
    <Card className="flex min-w-0 flex-col">
      <CardHeader>
        <CardTitle className="text-sm font-semibold">
          {t('channels.title')}
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          {t('channels.subtitle')}
        </p>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-5">
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : ordered.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {t('empty')}
          </p>
        ) : (
          <>
            {/* A 2px surface gap does the separating between segments — never a stroke, which
                would add data-weight ink that is not data. */}
            <div
              className="flex h-3.5 gap-[2px] overflow-hidden rounded-full"
              aria-hidden="true"
            >
              {ordered.map((row) => (
                <div
                  key={row.key}
                  style={{
                    flexGrow: value(row),
                    backgroundColor: CHANNEL_COLOR[row.key],
                  }}
                />
              ))}
            </div>

            <ul className="flex flex-col gap-3.5">
              {ordered.map((row) => (
                <li key={row.key} className="flex items-baseline gap-2.5">
                  <span
                    className="mt-0.5 size-2.5 shrink-0 rounded-sm"
                    style={{ backgroundColor: CHANNEL_COLOR[row.key] }}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium">
                      {t(`channels.${row.key}`, { defaultValue: row.key })}
                    </div>
                    <div className="text-xs tabular-nums text-muted-foreground">
                      {t('channels.bonSummary', {
                        bons: formatNumber(row.bons),
                        average: formatCurrency(
                          row.bons > 0 ? row.revenueCents / row.bons : 0,
                        ),
                      })}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold tabular-nums">
                      {format(value(row))}
                    </div>
                    <div className="text-xs tabular-nums text-muted-foreground">
                      {totalValue > 0
                        ? formatShare(value(row) / totalValue)
                        : '—'}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="mt-auto rounded-lg bg-muted/60 p-3 text-xs leading-5 text-muted-foreground">
          {t('channels.tableNote')}
        </div>

        <Link
          to={detailsTo}
          search={detailsSearch}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          {t('details')}
          <ArrowRight className="h-3 w-3" aria-hidden="true" />
        </Link>
      </CardContent>
    </Card>
  )
}

const shareFormatter = new Intl.NumberFormat('de-AT', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

export function formatShare(share: number): string {
  return shareFormatter.format(share)
}
